import type { HardcodedLinkSourceValue } from 'common/constants/attribution-link-source';
import { Marketplaces } from 'common/constants/enums';
import { Product } from 'common/interfaces/agg-page';
import { TemporaryLinkService } from 'common/services/temporary-link-service.api';
import { PageFormProduct } from 'store/api/pages-api/pages-api-types';
import { ProductTemporaryLink } from 'store/reducers/temporary-links/temporary-links-slice';

interface TemporaryLinksResponse {
  count: number;
  tempLinks: ProductTemporaryLink[];
}

const fetchTemporaryLinkForProduct = async (
  productId: string,
  marketplace: Marketplaces,
  publisherId: number | undefined,
  region: string,
): Promise<ProductTemporaryLink | null> => {
  try {
    const query = {
      limit: 1,
      offset: 0,
      region,
      ...(marketplace === Marketplaces.Walmart
        ? { w_item_ids: [productId] }
        : { asins: [productId] }),
    };

    const { data } = await TemporaryLinkService.getPublisherLinks(
      query,
      publisherId || undefined,
    );

    return (data as TemporaryLinksResponse).tempLinks?.[0] ?? null;
  } catch {
    return null;
  }
};

export const enrichProductWithTemporaryLink = async (
  product: Product,
  marketplace: Marketplaces,
  publisherId: number | undefined,
  region: string,
): Promise<Product> => {
  const baseProduct: Product = {
    ...product,
    is_hardcoded_link: product.is_hardcoded_link ?? 0,
  };

  const tempLink = await fetchTemporaryLinkForProduct(
    String(product.product_id),
    marketplace,
    publisherId,
    region,
  );

  if (!tempLink?.link) {
    return baseProduct;
  }

  return {
    ...baseProduct,
    hardcoded_link_source: (tempLink.source ?? null) as HardcodedLinkSourceValue | null,
    is_hardcoded_link: 1,
    page_url: tempLink.link,
  };
};

export const enrichPageFormProductWithTemporaryLink = async (
  product: PageFormProduct,
  marketplace: Marketplaces,
  publisherId: number | undefined,
  region: string,
): Promise<PageFormProduct> => {
  const baseProduct: PageFormProduct = {
    ...product,
    isHardcodedLink: product.isHardcodedLink ?? 0,
  };

  const tempLink = await fetchTemporaryLinkForProduct(
    String(product.product_id),
    marketplace,
    publisherId,
    region,
  );

  if (!tempLink?.link) {
    return baseProduct;
  }

  return {
    ...baseProduct,
    hardcodedLinkSource: tempLink.source ?? null,
    isHardcodedLink: 1,
    pageUrl: tempLink.link,
  };
};
